import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { API_CONFIG } from '../config/api';
import toast from 'react-hot-toast';
import { showToastSuccess } from '../utils/toastUtils';

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  userType: string;
  adminLevel?: string;
  centerId?: number;
  branchId?: number;
  departmentId?: number;
  isActive?: boolean;
  center?: {
    id: number;
    name: string;
    code: string;
    phone?: string;
    email?: string;
  };
  branch?: {
    id: number;
    name: string;
    code: string;
    phone?: string;
    email?: string;
  };
  department?: {
    id: number;
    name: string;
    code: string;
    description?: string;
  };
  role: {
    name: string;
    permissions: Array<{
      id: number;
      name: string;
      key: string;
      resource: string;
      action: string;
      category: string;
    }>;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  hasPermission: (permissionKey: string) => boolean;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Note: Auth context uses manual fetch for user profile
  // This is appropriate as auth state is critical and managed separately
  // React Query migration is optional for auth contexts
  
  // Function to fetch user profile
  const fetchUserProfile = async (authToken: string) => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/profile`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Extract centerId from center object if available
          const userData = {
            ...data.data,
            centerId: data.data.center?.id || data.data.centerId,
            branchId: data.data.branch?.id || data.data.branchId,
            departmentId: data.data.department?.id || data.data.departmentId
          };
          setUser(userData);
        }
      } else {
        // If profile fetch fails, clear the invalid token
        console.error('Failed to fetch user profile, clearing token');
        setToken(null);
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Clear token on error to prevent infinite loops
      setToken(null);
      localStorage.removeItem('authToken');
    }
  };

  const refreshUserProfile = async () => {
    if (token) {
      await fetchUserProfile(token);
    }
  };

  useEffect(() => {
    // Check for existing authentication on app load
    // Skip auth check on login page to optimize performance
    if (window.location.pathname === '/login') {
      setIsLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('authToken');

    if (storedToken) {
      setToken(storedToken);
      // Fetch user profile instead of using stored user data
      fetchUserProfile(storedToken).finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (newToken: string, userData: User) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('authToken', newToken);
    // Don't store user data in localStorage anymore - fetch from API
    // For now, use the provided user data, but could refresh profile later
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    
    // Show simple toast instead of using translations to avoid loading en.json
    toast.success('Successfully logged out', {
      duration: 3000,
      position: 'top-right',
    });
  };

  const hasPermission = (permissionKey: string): boolean => {
    if (!user || !user.role || !user.role.permissions) return false;
    return user.role.permissions.some(permission => permission.key === permissionKey);
  };

  const value = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    hasPermission,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
